import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderUsageLogOut } from '../../../types/api';
import { apiService } from '../../../services/api';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import './OrderActivityLog.scss';

interface OrderActivityLogProps {
  orderId: number;
  // Changes when the order changes (e.g. its updated_at) to reload the log
  refreshKey?: string | null;
}

/** Who did what to an order: QR scans and staff actions (open, deliver...). */
const OrderActivityLog: React.FC<OrderActivityLogProps> = ({ orderId, refreshKey }) => {
  const { t } = useTranslation();
  const { formatPurchaseDateTime } = useDateFormatter();
  const [logs, setLogs] = useState<OrderUsageLogOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiService.getOrderLogs(orderId)
      .then((result) => {
        if (!cancelled) setLogs(result.logs);
      })
      .catch((err) => {
        console.error('Failed to fetch order logs:', err);
        if (!cancelled) setError(t('bartender.orderDetail.logsFetchError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, refreshKey, t]);

  if (loading && logs.length === 0) {
    return <div className="order-activity-log__note">{t('bartender.orderDetail.loadingLogs')}</div>;
  }
  if (error) {
    return <div className="order-activity-log__note">{error}</div>;
  }
  if (logs.length === 0) {
    return <div className="order-activity-log__note">{t('bartender.orderDetail.noLogs')}</div>;
  }

  return (
    <div className="order-activity-log">
      {logs.map((log) => {
        const logTime = formatPurchaseDateTime(log.used_at);
        const action = log.action_type || '';
        return (
          <div key={log.id} className="order-activity-log__row">
            <span className={`order-activity-log__status order-activity-log__status--${log.success ? 'success' : 'error'}`}>
              {log.success ? t('bartender.orderDetail.logSuccess') : t('bartender.orderDetail.logFailed')}
            </span>
            <span className="order-activity-log__action">
              {action ? t(`orderActivity.actions.${action}`, { defaultValue: action }) : '—'}
            </span>
            {log.venue_name && (
              <span className="order-activity-log__venue">{log.venue_name}</span>
            )}
            <span className="order-activity-log__who">
              {log.user_name || t('orderActivity.customer')}
            </span>
            <span className="order-activity-log__time">
              {logTime.date} {logTime.time}
            </span>
            {log.ip_address && (
              <span className="order-activity-log__ip">{log.ip_address}</span>
            )}
            {log.error_message && !log.success && (
              <span className="order-activity-log__error">{log.error_message}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OrderActivityLog;
